<template>
  <div class="flex flex-col">
    <span class="text-sm border-b text-gray-400">Student Transfer Dates</span>
    <div class="p-2 rounded-md bg-gray-100 shadow-sm">
      <div class="relative p-2 rounded-md bg-gray-50">
        <div class="absolute right-2 flex-row">
          <button class="px-1 rounded-sm text-sm font-italic text-gray-400 bg-gray-200 hover:bg-gray-300"
            @click="loadTransferDates">
            Refresh
          </button>
        </div>
        <div class="p-2 flex flex-col gap-2">
          <div class="flex flex-row">
            <MultiCheckboxField
              alignment="col"
              :options="checkboxOptions"
              v-model="formValues.selectedCheckboxOptions"
            >
            Filters
          </MultiCheckboxField>
          </div>
          <div class="grid grid-cols-3 gap-2"
            v-if="showFilterCreatedAt">
            <CalendarField
              v-model="formValues.filterCreatedAtStartDate">
              Created At Start
            </CalendarField>
            <CalendarField
              v-model="formValues.filterCreatedAtEndDate">
              To End Date (exclusive)
            </CalendarField>
          </div>
          <div class="grid grid-cols-3 gap-2"
            v-if="showFilterTransferDates">
            <CalendarField
              v-model="formValues.filterTransferStartDate">
              Transfer Date Start
            </CalendarField>
            <CalendarField
              v-model="formValues.filterTransferEndDate">
              To End Date (exclusive)
            </CalendarField>
          </div>
          <div class="grid grid-cols-3 gap-2">
            <TextField
              v-if="showFilterStudents"
              v-model="formValues.filterStudents">
              Student Field Filter
            </TextField>
            <VSelectionField class="col-span-2"
              v-if="showFilterPartitions"
              :multiple="true"
              v-model="formValues.selectedPartitions"
              :options="partitionOptions">
              Partitions
            </VSelectionField>
          </div>
          <div class="grid grid-cols-4 gap-2">
            <div class="flex flex-col gap-1">
              <Button class="bg-gray-200 hover:bg-gray-50"
                :class="{
                  'bg-sky-600 hover:bg-sky-400 text-white': selectAllToggle,
                }"
                @click="onSelectAll">
                Select All
              </Button>
              <Button class="bg-gray-200 hover:bg-gray-50"
                @click="onDeselectAll">
                Deselect All
              </Button>
            </div>
            <div class="col-span-2">
              Number Selected: {{ this.selectedTransferDates.length }}
            </div>
            <Button class="flex flex-row items-center justify-center bg-red-600 hover:bg-red-400 text-white"
              ref="deleteAllButton"
              @click="onDeleteSelected">
              Delete Selected
              <Spinner
                v-if="displayDeleteSpinner"
                :size="8"
              />
            </Button>
          </div>
          <Button class="mt-2"
            v-if="confirmDeletion"
            color="blue"
            v-outside-click="{
              exclude: ['deleteAllButton'],
              handler: 'cancelDeletion'
            }"
            :disabled="!confirmDeletionButtonEnabled"
            @click="onDeletionConfirmed">
            Confirm Deletion
          </Button>
        </div>
        <div class="flex relative justify-center items-center mt-4">
          <PaginationButton
            ref="tableTop"
            :pages="pages"
            :initPage="currentPage"
            @toPage="onToPage"
          />
          <div class="absolute right-0 text-gray-400 mt-1 text-sm">
            Total Entries: {{ totalTransferDates }}
          </div>
        </div>
        <Spinner
          v-if="displayLoadingTransferDatesSpinner"
          message="Loading Transfer Dates"
          :size="16"
        />
        <DataTable
          ref="transferDatesTable"
          :tableHeads="transferDatesTableHeads"
          :dataKeyList="transferDatesTableKeys"
          :dataItems="transferDates"
          uniqueKeyValue="id"
          @toggle="onToggleTransferDate"
          @toggleOpen="onToggleOpenTable"
        />
        <div class="flex flex-col justify-center pb-4" v-if="openTable && transferDates.length > 0">
          <Button class="text-gray-300 text-sm w-full hover:bg-gray-100"
            @click="scrollToTableTop">
            Scroll To Top
          </Button>
          <div class="flex flex-row justify-center">
            <PaginationButton
              :pages="pages"
              :initPage="currentPage"
              @toPage="onToPage"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataTable from '../DataTable.vue'

import PaginationButton from '../../PaginationButton.vue'
import Spinner from '../../Spinner.vue'

import CalendarField from '../../formComponents/CalendarField.vue'
import TextField from '../../formComponents/TextField.vue'
import VSelectionField from '../../formComponents/VSelectionField.vue'

import MultiCheckboxField from '../../formComponents/MultiCheckboxField.vue'

import Button from '../../Button.vue'

import user from '../../../services/user/user.js'
import time from '../../../services/utils/time.js'
import partitions from '../../../services/partitions.js'

import transferDateService from '../../../services/activeSync/transferDates.js'

export default {
  components: {
    DataTable,
    PaginationButton,
    Spinner,
    CalendarField,
    TextField,
    VSelectionField,
    MultiCheckboxField,
    Button,
  },
  data() {
    return {
      formValues: {
        selectedCheckboxOptions: [],
        filterCreatedAtStartDate: undefined,
        filterCreatedAtEndDate: undefined,
        filterTransferStartDate: undefined,
        filterTransferEndDate: undefined,
        filterStudents: undefined,
        selectedPartitions: [],
      },

      openTable: false,
      displayLoadingTransferDatesSpinner: false,

      transferDatesTableHeads: [
        // 'ID',
        'Transfer Date',
        'Created At',
        'Internal ID',
        'First Name',
        'Last Name',
      ],
      transferDatesTableKeys: [
        // 'id',
        'transferDate',
        'createdAt',
        'internalId',
        'firstName',
        'lastName',
      ],

      pages: undefined,
      totalTransferDates: 0,
      currentPage: 1,
      transferDates: [],

      selectedTransferDates: [],
      selectAllToggle: false,

      checkboxOptions: [
        'Created At Dates',
        'Transfer Dates',
        'Students',
        'Partitions',
      ],

      partitions: [],
      partitionOptions: undefined,

      confirmDeletion: false,
      confirmDeletionButtonEnabled: false,
      enableDeletionButtonDelay: undefined,

      formValuesChangeApiCallDelay: undefined,
      displayDeleteSpinner: false,
    }
  },
  methods: {
    callOnceWithDelay(callback, delayTime) {
      if (this.formValuesChangeApiCallDelay) {
        clearTimeout(this.formValuesChangeApiCallDelay)
      }
      this.formValuesChangeApiCallDelay = setTimeout(() => {
        callback()
      }, delayTime)
    },
    async loadTransferDates() {
      this.selectAllToggle = false
      let queryParams = this.createQueryParams()
      let serverData = await transferDateService.getData(queryParams)
      this.pages = serverData.pages
      this.totalTransferDates = serverData.page_data.num_transfer_dates
      this.transferDates = serverData.content.map(transferDateService.remapData)
    },
    createQueryParams() {
      let queryParams = {
        page: this.currentPage,
      }
      if (this.showFilterCreatedAt) {
        queryParams.createdAtStart = this.formValues.filterCreatedAtStartDate
        queryParams.createdAtEnd = this.formValues.filterCreatedAtEndDate
      }
      if (this.showFilterTransferDates) {
        queryParams.transferStart = this.formValues.filterTransferStartDate
        queryParams.transferEnd = this.formValues.filterTransferEndDate
      }
      if (this.showFilterStudents) {
        queryParams.studentFilter = this.formValues.filterStudents
      }
      if (this.showFilterPartitions) {
        queryParams.partitionFilter = this.formValues.selectedPartitions
      }
      return queryParams
    },
    onToPage(pageNumber) {
      this.currentPage = pageNumber
      this.loadTransferDates()
    },
    onToggleTransferDate(item, isAdd) {
      if (isAdd) {
        this.selectedTransferDates.push(item.id)
      }
      else {
        let index = this.selectedTransferDates.indexOf(item.id)
        if (index >= 0) {
          this.selectAllToggle = false
          this.selectedTransferDates.splice(index, 1)
        }
      }
    },
    onToggleOpenTable() {
      this.openTable = !this.openTable
    },
    scrollToTableTop() {
      let ref = this.$refs.tableTop
      ref.$el.scrollIntoView({
        behavior: 'smooth',
      })
    },
    onSelectAll() {
      this.selectAllToggle = true
      this.selectedTransferDates = this.selectedTransferDates.concat(this.transferDates.map(item => item.id))
      this.selectedTransferDates = [...new Set(this.selectedTransferDates)]
      this.$refs.transferDatesTable.selectAllEntries()
    },
    onDeselectAll() {
      this.selectAllToggle = false
      this.$set(this, 'selectedTransferDates', [])
      this.$refs.transferDatesTable.clearSelectedEntries()
    },
    onDeleteSelected() {
      this.confirmDeletion = true
      this.enableDeletionButtonDelay = setTimeout(() => {
        this.confirmDeletionButtonEnabled = true
      }, 2000)
    },
    cancelDeletion() {
      this.confirmDeletion = false
      this.confirmDeletionButtonEnabled = false
      clearTimeout(this.enableDeletionButtonDelay)
    },
    async onDeletionConfirmed() {
      try {
        this.displayDeleteSpinner = true
        await transferDateService.deleteByIds(this.selectedTransferDates)
        this.displayDeleteSpinner = false
        this.confirmDeletion = false
        this.confirmDeletionButtonEnabled = false
        this.selectedTransferDates = []
        this.loadTransferDates()
      }
      catch (error) {
        // do nothing
      }
    },
  },
  async mounted() {
    await user.ready()
    this.formValues.filterCreatedAtStartDate = time.today()
    this.formValues.filterTransferStartDate = time.today()
    this.formValues.selectedCheckboxOptions = ['Transfer Dates']
    let response = await partitions.getPartitionFromServer()
    this.partitionOptions = response.data.map((partitionData) => {
      return {
        label: partitionData.description,
        value: partitionData.id,
      }
    })
  },
  computed: {
    showFilterCreatedAt() {
      return this.formValues.selectedCheckboxOptions.includes('Created At Dates')
    },
    showFilterTransferDates() {
      return this.formValues.selectedCheckboxOptions.includes('Transfer Dates')
    },
    showFilterStudents() {
      return this.formValues.selectedCheckboxOptions.includes('Students')
    },
    showFilterPartitions() {
      return this.formValues.selectedCheckboxOptions.includes('Partitions')
    },
  },
  watch: {
    formValues: {
      deep: true,
      handler() {
        this.callOnceWithDelay(() => this.loadTransferDates(), 1000)
      },
    }
  },
}
</script>
